import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactSlider from "react-slider";
import { Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PropertyList from "./PropertyListCarolineMap";
import Header from "./Header";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYWJoaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

// Debounce hook to prevent rapid API calls
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};


//  Add helper functions at the top of PropertyListCaroline.js:
const formatCurrency = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? `$${val.toLocaleString("en-US")}` : "N/A";
};

const formatArea = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(3) + " ac" : "N/A";
};

const formatNumber = (num, decimals = 0) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(decimals) : "N/A";
};

const formatPercent = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(2) + "%" : "N/A";
};



// MAIN COMPONENT

const ParcelScreenCarolinaMap = () => {
  
  // REFS
  const initialFetchDone = useRef(false);
  
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const geocoderContainerRef = useRef(null);

  
  // STATE - DATA
  
  const [parcelData, setParcelData] = useState([]);
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  
  // STATE - VIEW
  
  const [showMap, setShowMap] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(
    localStorage.getItem("isSubscribed") === "true"
  );

  
  // STATE - FILTER DROPDOWNS
  
  const [showPriceFilter, setShowPriceFilter] = useState(false);
  const [showAreaFilter, setShowAreaFilter] = useState(false);
  const [showZestimateFilter, setShowZestimateFilter] = useState(false);
  const [showSpreadFilter, setShowSpreadFilter] = useState(false);
  const [showZoneFilter, setShowZoneFilter] = useState(false);
  const [showCountyFilter, setShowCountyFilter] = useState(false);
  const [showSortFilter, setShowSortFilter] = useState(false);

  
  // STATE - FILTER VALUES
  
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [areaRange, setAreaRange] = useState([0, 500000]);
  const [zestimateRange, setZestimateRange] = useState([0, 2000000]);
  const [spreadRange, setSpreadRange] = useState([-100, 1000]);
  const [zoneCodeFilter, setZoneCodeFilter] = useState("All");
  const [countyFilter, setCountyFilter] = useState("wake");
  const [isVacant, setIsVacant] = useState(true);
  const [listedSaleFilter, setListedSaleFilter] = useState("ActiveListing");
  const [showOnlyListed, setShowOnlyListed] = useState(false);

  const [showMaxUnitFilter, setShowMaxUnitFilter] = useState(false);
  const [maxUnitRange, setMaxUnitRange] = useState([0, 100]);
  const [sliderMinMaxUnit, setSliderMinMaxUnit] = useState(0);
  const [sliderMaxMaxUnit, setSliderMaxMaxUnit] = useState(100);


  
  // STATE - SORT
  
  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState("desc");

  
  // STATE - SLIDER LIMITS
  
  const [sliderMinPrice, setSliderMinPrice] = useState(0);
  const [sliderMaxPrice, setSliderMaxPrice] = useState(1000000);
  const [sliderMinArea, setSliderMinArea] = useState(0);
  const [sliderMaxArea, setSliderMaxArea] = useState(500000);
  const [sliderMinZestimate, setSliderMinZestimate] = useState(0);
  const [sliderMaxZestimate, setSliderMaxZestimate] = useState(2000000);
  const [sliderMinSpread, setSliderMinSpread] = useState(-100);
  const [sliderMaxSpread, setSliderMaxSpread] = useState(1000);

    //  DEBOUNCE FILTER RANGES
  const debouncedPriceRange = useDebounce(priceRange, 1000);
  const debouncedAreaRange = useDebounce(areaRange, 1000);
  const debouncedZestimateRange = useDebounce(zestimateRange, 1000);
  const debouncedSpreadRange = useDebounce(spreadRange, 1000);
  const debouncedMaxUnitRange = useDebounce(maxUnitRange, 1000);
  
  // CONSTANTS
  
  const countyOptions = ["wake", "chatham"];
  const listedSaleOptions = ["ActiveListing", "PendingSale"];
  const sortOptions = [
    { value: "none", label: "No Sorting" },
    // { value: "totalunits", label: "Max Unit" },
    { value: "totallandvalue", label: "Developed Value" },
    { value: "spreadpercentage", label: "Spread %" },
  ];

  const FLOOD_ZONE_LEGEND = [
    { zone: "X", color: "#4CAF50", label: "X - Minimal Risk" },
    { zone: "AE", color: "#FF9800", label: "AE - High Risk + BFE" },
    { zone: "VE", color: "#E91E63", label: "VE - Coastal + BFE" },
    { zone: "A", color: "#2196F3", label: "A - High Risk (No BFE)" },
  ];

  
  // FUNCTION - FETCH DATA FROM API
  
  const fetchParcelData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("📥 Fetching parcel data from API...");

      // Build query parameters
      const params = new URLSearchParams();
      // if (isVacant) params.append("is_vaccant", "1");
          if (isVacant) {
      params.append("is_vaccant", "1");
      // Always include listedsale when vacant is true

      // params.append("listedsale", listedSaleFilter);
        // Only add listedsale filter if checkbox is checked
      if (showOnlyListed && listedSaleFilter) {
        params.append("listedsale", listedSaleFilter);
      }
    } else {
      params.append("is_vaccant", "0");  //  Pass 0 when unchecked
    }
      if (countyFilter && countyFilter !== "All") params.append("county", countyFilter);
      if (showOnlyListed && listedSaleFilter) params.append("listedsale", listedSaleFilter);

       //  Zone Code Filter
    if (zoneCodeFilter && zoneCodeFilter !== "All") {
      params.append("zonedcodelocal", zoneCodeFilter);
    }

    //  Max Unit Filter (only if vacant)
    if (isVacant && sliderMaxMaxUnit > 0 && (maxUnitRange[0] !== sliderMinMaxUnit || maxUnitRange[1] !== sliderMaxMaxUnit)) {
      if (maxUnitRange[0] > sliderMinMaxUnit) {
        params.append("noallowableunits_min", Math.round(maxUnitRange[0]));
      }
      if (maxUnitRange[1] < sliderMaxMaxUnit) {
        params.append("noallowableunits_max", Math.round(maxUnitRange[1]));
      }
    }

    // Zestimate (Price) Filter
    if (sliderMaxZestimate > 0 && (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate)) {
      if (zestimateRange[0] > sliderMinZestimate) {
        params.append("zestimate_min", Math.round(zestimateRange[0]));
      }
      if (zestimateRange[1] < sliderMaxZestimate) {
        params.append("zestimate_max", Math.round(zestimateRange[1]));
      }
    }

       //  Parcel Size (Area) Filter
    if (sliderMaxArea > 0 && (areaRange[0] !== sliderMinArea || areaRange[1] !== sliderMaxArea)) {
      if (areaRange[0] > sliderMinArea) {
        params.append("parcelsize_min", areaRange[0].toFixed(2));
      }
      if (areaRange[1] < sliderMaxArea) {
        params.append("parcelsize_max", areaRange[1].toFixed(2));
      }
    }

    //  Total Land Value Filter
    if (sliderMaxPrice > 0 && (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice)) {
      if (priceRange[0] > sliderMinPrice) {
        params.append("totallandvalue_min", Math.round(priceRange[0]));
      }
      if (priceRange[1] < sliderMaxPrice) {
        params.append("totallandvalue_max", Math.round(priceRange[1]));
      }
    }

    //  Spread Percentage Filter
    if (sliderMaxSpread > 0 && (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread)) {
      if (spreadRange[0] > sliderMinSpread) {
        params.append("spreadpercentage_min", spreadRange[0].toFixed(2));
      }
      if (spreadRange[1] < sliderMaxSpread) {
        params.append("spreadpercentage_max", spreadRange[1].toFixed(2));
      }
    }

   
    // sortBy should be one of: totalunits, spread, spreadpercentage, zestimate, totallandvalue
    if (sortBy) {
      params.append("sort_by", sortBy);
    }
    if (sortDirection) {
      params.append("sort_order", sortDirection); // 'asc' or 'desc'
    }

      const url = `https://beta.flowlogic.com/api/parcels/list-parcel-data/?${params.toString()}`;
      console.log(" API URL:", url);

      const token = localStorage.getItem("authToken");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(" API Response:", data);

      const features = data.features || [];
      console.log(` Loaded ${features.length} parcels`);

      setParcelData(features);
      setFilteredParcels(features);
      // updateSliderRanges(features);

      if (!initialFetchDone.current) {
  updateSliderRanges(features);
  initialFetchDone.current = true;
}
    } catch (err) {
      console.error(" Error fetching parcel data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  
  // FUNCTION - UPDATE SLIDER RANGES
  
  const updateSliderRanges = (features) => {
    if (features.length === 0) return;

    // Land Value
    const landValues = features
      .map((f) => parseFloat(f.properties?.landvalueperunit) || 0)
      .filter((v) => v > 0);
    if (landValues.length > 0) {
      const minPrice = Math.min(...landValues);
      const maxPrice = Math.max(...landValues);
      setSliderMinPrice(minPrice);
      setSliderMaxPrice(maxPrice);
      setPriceRange([minPrice, maxPrice]);
    }
    else {
    //  When no valid values, set to 0 to disable filter
    console.log("No valid Land Values found");
    setSliderMinPrice(0);
    setSliderMaxPrice(0);
    setPriceRange([0, 0]);
  }

    // Area
    const areaValues = features
      .map((f) => parseFloat(f.properties?.arealotsf) || 0)
      .filter((v) => v > 0);
    if (areaValues.length > 0) {
      const minArea = Math.min(...areaValues);
      const maxArea = Math.max(...areaValues);
      setSliderMinArea(minArea);
      setSliderMaxArea(maxArea);
      setAreaRange([minArea, maxArea]);
    }
    else {
    // console.log("No valid Area values found");
    setSliderMinArea(0);
    setSliderMaxArea(0);
    setAreaRange([0, 0]);
  }

    // Zestimate
    const zestimateValues = features
      .map((f) => parseFloat(f.properties?.zestimate) || 0)
      .filter((v) => v > 0);
    if (zestimateValues.length > 0) {
      const minZestimate = Math.min(...zestimateValues);
      const maxZestimate = Math.max(...zestimateValues);
      setSliderMinZestimate(minZestimate);
      setSliderMaxZestimate(maxZestimate);
      setZestimateRange([minZestimate, maxZestimate]);
    }
    else {
    // console.log("No valid Zestimate values found");
    setSliderMinZestimate(0);
    setSliderMaxZestimate(0);
    setZestimateRange([0, 0]);
  }

    // Spread
    const spreadValues = features
      .map((f) => parseFloat(f.properties?.spreadpercentage) || 0)
      .filter((v) => v !== 0);
    if (spreadValues.length > 0) {
      const minSpread = Math.min(...spreadValues);
      const maxSpread = Math.max(...spreadValues);
      setSliderMinSpread(minSpread);
      setSliderMaxSpread(maxSpread);
      setSpreadRange([minSpread, maxSpread]);
    }
    else {
    // console.log("No valid Spread values found");
    setSliderMinSpread(0);
    setSliderMaxSpread(0);
    setSpreadRange([0, 0]);
  }

  // const maxUnitValues = features
  //   .map((f) => parseFloat(f.properties?.noallowableunits || f.properties?.totalunits) || 0)
  //   .filter((v) => v > 0);
  // if (maxUnitValues.length > 0) {
  //   const minMaxUnit = Math.min(...maxUnitValues);
  //   const maxMaxUnit = Math.max(...maxUnitValues);
  //   setSliderMinMaxUnit(minMaxUnit);
  //   setSliderMaxMaxUnit(maxMaxUnit);
  //   setMaxUnitRange([minMaxUnit, maxMaxUnit]);
  // }
  // };

    // const maxUnitValues = features
    // .map((f) => {
    //   const props = f.properties || {};
    //   // Try all possible field names
    //   const unit = parseFloat(
    //     props.noallowableunits || 
    //     props.totalunits || 
    //     0
    //   );
    //   // console.log('Max Unit value:', unit, 'from properties:', props); 
    // })
    // .filter((v) => v > 0);

 
    


    //   const maxUnitValues = features
    // .map((f) => {
    //   const props = f.properties || {};
    //   // Try all possible field names
    //   const unit = parseFloat(
    //     props.noallowableunits || 
    //     props.totalunits || 
    //     props.total_units ||
    //     props.max_units ||
    //     props.maxunits ||
    //     props.units ||
    //     0
    //   );
    //   // console.log('Max Unit value:', unit, 'from properties:', props); 
    //   return unit;
    // })
    // .filter((v) => v > 0);

  // console.log('All Max Unit values:', maxUnitValues); 

    //  const maxUnitValues = features
    // .map((f) => {
    //   const props = f.properties || {};
    //   const unit = parseFloat(props.noallowableunits || 0);
    //   return unit;
    // })
    // .filter((v) => v > 0);

      // Max Unit
  const maxUnitValues = features
    .map((f) => parseFloat(f.properties?.noallowableunits || 0))
    .filter((v) => v > 0);

  if (maxUnitValues.length > 0) {
    const minMaxUnit = Math.floor(Math.min(...maxUnitValues));
    const maxMaxUnit = Math.ceil(Math.max(...maxUnitValues));
    console.log('Max Unit range:', minMaxUnit, '-', maxMaxUnit); 
    setSliderMinMaxUnit(minMaxUnit);
    setSliderMaxMaxUnit(maxMaxUnit);
    setMaxUnitRange([minMaxUnit, maxMaxUnit]);
  } else {
    // If no valid values, set defaults
    // console.warn('No valid Max Unit values found, using defaults');
    setSliderMinMaxUnit(0);
    setSliderMaxMaxUnit(100);
    setMaxUnitRange([0, 100]);
  }
};

  
  // FUNCTION - UPDATE MAP DATA
  
  const updateMapData = (parcels) => {
    try {
      if (mapRef.current?.getSource("parcel-source")) {
        mapRef.current.getSource("parcel-source").setData({
          type: "FeatureCollection",
          features: parcels,
        });
        fitMapToParcels(parcels);
      }
    } catch (e) {
      console.error("❌ Error updating map:", e);
    }
  };

  
  // FUNCTION - FIT MAP TO PARCELS
  
  const fitMapToParcels = (parcels) => {
    if (!mapRef.current || parcels.length === 0) return;

    try {
      const coords = parcels
        .map((f) => {
          const lon = parseFloat(f.properties?.longitude);
          const lat = parseFloat(f.properties?.latitude);
          if (isNaN(lon) || isNaN(lat)) return null;
          return [lon, lat];
        })
        .filter(Boolean);

      if (coords.length === 0) return;

      const lons = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);

      mapRef.current.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { padding: 50, duration: 1500, maxZoom: 14 }
      );
    } catch (err) {
      console.error("❌ Error fitting bounds:", err);
    }
  };

  
  // FUNCTION - HANDLE ROW CLICK
  
  const handleRowClick = (property, index) => {
    setSelectedRow(index);

    if (!showMap || !mapRef.current) return;

    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);

    if (isNaN(lat) || isNaN(lng)) return;

    mapRef.current.flyTo({
      center: [lng, lat],
      zoom: 17,
      duration: 1500,
    });

new mapboxgl.Popup()
  .setLngLat([lng, lat])
  .setHTML(`
    <div style="background:#fff; padding:14px; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15); font-family:Inter, sans-serif; font-size:13px; width:240px;">
      <h3 style="margin:0 0 8px 0; color:#1565C0; font-size:15px;">${property.addrline1 || "N/A"}</h3>
      <hr style="border:none; border-top:1px solid #eee; margin:8px 0;">
      <p><strong>County:</strong> ${property.county || "N/A"}</p>
      <p><strong>Parcel:</strong> ${property.parcel || "N/A"}</p>
      <p><strong>Size:</strong> ${formatArea(property.parcelsize)}</p>
      <p><strong>Zoning:</strong> ${property.zonedcodelocal || "N/A"}</p>
      <p><strong>Flood Zone:</strong> ${property.parcelcustomfemaflood || "N/A"}</p>
      <p><strong>Max Units:</strong> ${formatNumber(property.noallowableunits)}</p>
      <p><strong>Price:</strong> ${formatCurrency(property.zestimate)}</p>
      <p><strong>Spread:</strong> ${formatPercent(property.spreadpercentage)}</p>
    </div>
  `)
  .addTo(mapRef.current);
  };

  
  // FUNCTION - CLEAR ALL FILTERS
  
  const clearAllFilters = () => {
    console.log(" Clearing all filters...");

    // Reset API filters
    setCountyFilter("wake");
    setIsVacant(true);
    setShowOnlyListed(false);
    // setListedSaleFilter("ActiveListing");
    setListedSaleFilter("All");

    // Reset client-side filters
    setPriceRange([sliderMinPrice, sliderMaxPrice]);
    setAreaRange([sliderMinArea, sliderMaxArea]);
    setZestimateRange([sliderMinZestimate, sliderMaxZestimate]);
    setSpreadRange([sliderMinSpread, sliderMaxSpread]);
    // setMaxUnitRange([sliderMinMaxUnit, sliderMaxMaxUnit]); 

    //  Only reset max unit if it has values
    if (sliderMaxMaxUnit > 0) {
      setMaxUnitRange([sliderMinMaxUnit, sliderMaxMaxUnit]);
    }
    setZoneCodeFilter("All");
    setSortBy("none");

    // Close dropdowns
    setShowPriceFilter(false);
    setShowAreaFilter(false);
    setShowZestimateFilter(false);
    setShowSpreadFilter(false);
    setShowMaxUnitFilter(false); 
    setShowZoneFilter(false);
    setShowCountyFilter(false);
    setShowSortFilter(false);
    setShowMap(false);
  };

  
  // FUNCTION - FILTER ACTIVE CHECKS

  const isPriceActive = () => {
  if (sliderMaxPrice === 0) return false; // No valid prices
  return (
    sliderMinPrice !== undefined &&
    sliderMaxPrice !== undefined &&
    (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice)
  );
};

const isAreaActive = () => {
  if (sliderMaxArea === 0) return false; // No valid areas
  return (
    sliderMinArea !== undefined &&
    sliderMaxArea !== undefined &&
    (areaRange[0] !== sliderMinArea || areaRange[1] !== sliderMaxArea)
  );
};

const isZestimateActive = () => {
  if (sliderMaxZestimate === 0) return false; // No valid zestimates
  return (
    sliderMinZestimate !== undefined &&
    sliderMaxZestimate !== undefined &&
    (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate)
  );
};

const isSpreadActive = () => {
  if (sliderMaxSpread === 0) return false; // No valid spreads
  return (
    sliderMinSpread !== undefined &&
    sliderMaxSpread !== undefined &&
    (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread)
  );
};

const isMaxUnitActive = () => {
  if (sliderMaxMaxUnit === 0) return false; // No valid max units
  return (
    sliderMinMaxUnit !== undefined &&
    sliderMaxMaxUnit !== undefined &&
    (maxUnitRange[0] !== sliderMinMaxUnit || maxUnitRange[1] !== sliderMaxMaxUnit)
  );
};

  
//   const isPriceActive = () =>
//     sliderMinPrice !== undefined &&
//     sliderMaxPrice !== undefined &&
//     (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice);

//   const isAreaActive = () =>
//     sliderMinArea !== undefined &&
//     sliderMaxArea !== undefined &&
//     (areaRange[0] !== sliderMinArea || areaRange[1] !== sliderMaxArea);

//   // const isMaxUnitActive = () =>
//   //   sliderMinMaxUnit !== undefined &&
//   //   sliderMaxMaxUnit !== undefined &&
//   //   (maxUnitRange[0] !== sliderMinMaxUnit || maxUnitRange[1] !== sliderMaxMaxUnit);
//   const isMaxUnitActive = () => {
//   //  Only show as active if max units exist AND range is changed
//   if (sliderMaxMaxUnit === 0) return false; // No max units available
//   return (
//     sliderMinMaxUnit !== undefined &&
//     sliderMaxMaxUnit !== undefined &&
//     (maxUnitRange[0] !== sliderMinMaxUnit || maxUnitRange[1] !== sliderMaxMaxUnit)
//   );
// };

//   const isZestimateActive = () =>
//     sliderMinZestimate !== undefined &&
//     sliderMaxZestimate !== undefined &&
//     (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate);

//   const isSpreadActive = () =>
//     sliderMinSpread !== undefined &&
//     sliderMaxSpread !== undefined &&
//     (spreadRange[0] !== sliderMinSpread || spreadRange[1] !== sliderMaxSpread);

  const isZoneActive = () => zoneCodeFilter && zoneCodeFilter !== "All";
  const isCountyActive = () => countyFilter && countyFilter !== "wake";
  const isSortActive = () => sortBy && sortBy !== "none";
  const canShowMap = () => countyFilter && countyFilter !== "All";

  
  // FUNCTION - UI HANDLERS
  
  const toggleMenu = () => setIsMenuOpen((s) => !s);
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.href = "/";
  };
  const handleTabChange = () => {};

  
  // EFFECT - FETCH DATA ON MOUNT & FILTER CHANGE
  
  // useEffect(() => {
  //   fetchParcelData();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [countyFilter, isVacant, showOnlyListed, listedSaleFilter,zoneCodeFilter,maxUnitRange,priceRange,areaRange,zestimateRange,spreadRange]);

  useEffect(() => {
  fetchParcelData();
}, [
  countyFilter,
  isVacant,
  showOnlyListed,
  listedSaleFilter,
  zoneCodeFilter,
  debouncedMaxUnitRange,   
  debouncedPriceRange,      
  debouncedAreaRange,       
  debouncedZestimateRange, 
  debouncedSpreadRange,   
  sortBy,       
  sortDirection ,    
]);

  // EFFECT - APPLY CLIENT-SIDE FILTERS
  
  useEffect(() => {
    console.log(" Applying client-side filters...");
    // console.log("Max Unit Range:", maxUnitRange);
    const filtered = parcelData.filter((f) => {
      const props = f.properties || {};
      const landValue = parseFloat(props.landvalueperunit) || 0;
      const area = parseFloat(props.arealotsf) || 0;
      const zestimate = parseFloat(props.zestimate) || 0;
      const spread = parseFloat(props.spreadpercentage) || 0;
      const zone = props.zonedcodelocal || "Unknown";
      const maxUnit = parseFloat(props.noallowableunits || props.totalunits) || 0;

      // const matchLandValue = landValue >= priceRange[0] && landValue <= priceRange[1];
      // const matchArea = area >= areaRange[0] && area <= areaRange[1];
      // const matchZestimate = zestimate >= zestimateRange[0] && zestimate <= zestimateRange[1];
      // const matchSpread = spread >= spreadRange[0] && spread <= spreadRange[1];
      // const matchZone =
      //   zoneCodeFilter === "All" ||
      //   String(zone).toLowerCase() === String(zoneCodeFilter).toLowerCase();

      // const matchMaxUnit = maxUnit >= maxUnitRange[0] && maxUnit <= maxUnitRange[1];
      // const matchMaxUnit = maxUnit === 0 || (maxUnit >= maxUnitRange[0] && maxUnit <= maxUnitRange[1]);
     
    //     const matchMaxUnit = 
    //   sliderMaxMaxUnit === 0 || // Skip filter if no max units available
    //   maxUnit === 0 || // Allow parcels with no max unit value
    //   (maxUnit >= maxUnitRange[0] && maxUnit <= maxUnitRange[1]);
    //   if (!matchMaxUnit && maxUnit > 0) {
    //   console.log('Filtered out parcel with max unit:', maxUnit, 'range:', maxUnitRange);
    // }

     // ✅ FIXED: Skip filter if range is [0, 0] (no valid values)
    const matchLandValue = 
      (sliderMaxPrice === 0) || // Skip if no valid prices
      landValue === 0 || // Allow null values
      (landValue >= priceRange[0] && landValue <= priceRange[1]);

    const matchArea = 
      (sliderMaxArea === 0) || // Skip if no valid areas
      area === 0 || // Allow null values
      (area >= areaRange[0] && area <= areaRange[1]);

    const matchZestimate = 
      (sliderMaxZestimate === 0) || // Skip if no valid zestimates
      zestimate === 0 || // Allow null values
      (zestimate >= zestimateRange[0] && zestimate <= zestimateRange[1]);

    const matchSpread = 
      (sliderMaxSpread === 0) || // Skip if no valid spreads
      spread === 0 || // Allow null values
      (spread >= spreadRange[0] && spread <= spreadRange[1]);

    const matchMaxUnit = 
      (sliderMaxMaxUnit === 0) || // Skip if no valid max units
      maxUnit === 0 || // Allow null values
      (maxUnit >= maxUnitRange[0] && maxUnit <= maxUnitRange[1]);

      const matchZone =
      zoneCodeFilter === "All" ||
      String(zone).toLowerCase() === String(zoneCodeFilter).toLowerCase();

     
      return matchLandValue && matchArea && matchZestimate && matchSpread && matchZone  && matchMaxUnit;
    });

    console.log(` Filtered to ${filtered.length} parcels`);
    setFilteredParcels(filtered);

    // if (mapRef.current && mapInitialized) {
    //   updateMapData(filtered);
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [parcelData, priceRange, areaRange, zestimateRange, spreadRange, zoneCodeFilter, mapInitialized]);
  // }, [parcelData, priceRange, areaRange, zestimateRange, spreadRange, zoneCodeFilter, mapInitialized,maxUnitRange]);
  }, [parcelData, priceRange, areaRange, zestimateRange, spreadRange, zoneCodeFilter,maxUnitRange]);

  
  // COMPUTED - SORTED PARCELS
  
  const sortedParcels = useMemo(() => {
    if (sortBy === "none") return filteredParcels;

    const sorted = [...filteredParcels].sort((a, b) => {
      const aVal = parseFloat(a.properties?.[sortBy]) || 0;
      const bVal = parseFloat(b.properties?.[sortBy]) || 0;

      if (sortDirection === "asc") {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    console.log(` Sorted by ${sortBy} (${sortDirection})`);
    return sorted;
  }, [filteredParcels, sortBy, sortDirection]);

  
  // COMPUTED - ZONE CODES
  
  const zoneCodes = useMemo(() => {
    const codes = new Set();
    parcelData.forEach((f) => {
      const z = f.properties?.zonedcodelocal || "Unknown";
      if (z) codes.add(String(z));
    });
    return ["All", ...Array.from(codes).sort()];
  }, [parcelData]);

  
  // EFFECT - INITIALIZE MAP
  
  useEffect(() => {
    // if (!showMap || mapInitialized) return;
      if (!showMap || mapInitialized || mapRef.current) return;

    const initMap = async () => {
      try {
        console.log("🗺️ Initializing map...");

        mapRef.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: "mapbox://styles/mapbox/light-v11",
            //  style: "mapbox://styles/mapbox/satellite-streets-v12",
          center: [-78.6443, 35.7877],
          zoom: 12,
        });

        mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

        mapRef.current.on("load", () => {
          console.log("🗺️ Map loaded");

          // Add source
          mapRef.current.addSource("parcel-source", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: sortedParcels,
            },
          });

          // Add fill layer
          mapRef.current.addLayer({
            id: "parcel-fill",
            type: "fill",
            source: "parcel-source",
            paint: {
              "fill-color": [
                "match",
                ["get", "parcelcustomfemaflood"],
                "X",
                "#4CAF50",
                "AE",
                "#FF9800",
                "VE",
                "#E91E63",
                "A",
                "#2196F3",
                "#9fa2e7",
              ],
              "fill-opacity": 0.45,
            },
          });

          // Add outline layer
          mapRef.current.addLayer({
            id: "parcel-outline",
            type: "line",
            source: "parcel-source",
            paint: {
              "line-color": [
                "match",
                ["get", "parcelcustomfemaflood"],
                "X",
                "#4CAF50",
                "AE",
                "#FF9800",
                "VE",
                "#E91E63",
                "A",
                "#2196F3",
                "#9fa2e7",
              ],
              "line-width": 1.3,
            },
          });

          // Popup on click
          mapRef.current.on("click", "parcel-fill", (e) => {
            const props = e.features[0].properties;

            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(
                `
                <div style="background:#fff; padding:14px; border-radius:8px; box-shadow:0 2px 8px rgba(0,0,0,0.15); font-family:Inter, sans-serif; font-size:13px; width:240px;">
                  <h3 style="margin:0 0 6px 0; color:#1565C0; font-size:15px;">${props.addrline1 || "N/A"}</h3>
                  <hr style="border:none; border-top:1px solid #eee; margin:8px 0;">
                  <p><strong>County:</strong> ${props.county || "N/A"}</p>
                  <p><strong>Parcel:</strong> ${props.parcel || "N/A"}</p>
                  <p><strong>Size:</strong> ${formatArea(props.parcelsize)}</p>
                  <p><strong>Zoning:</strong> ${props.zonedcodelocal || "N/A"}</p>
                  <p><strong>Flood Zone:</strong> ${props.parcelcustomfemaflood || "N/A"}</p>
                  <p><strong>Vacant:</strong> ${props.is_vaccant === 1 ? "Yes" : "No"}</p>
                  <p><strong>Listed:</strong> ${props.listedsale || "Not listed"}</p>
                  <p><strong>Max Units:</strong> ${formatNumber(props.noallowableunits)}</p>
                  <p><strong>Price:</strong> ${formatCurrency(props.zestimate)}</p>
                  <p><strong>Spread:</strong> ${formatPercent(props.spreadpercentage)}</p>
                </div>
              `
              )
              .addTo(mapRef.current);
          });

          // Cursor change
          mapRef.current.on("mouseenter", "parcel-fill", () => {
            mapRef.current.getCanvas().style.cursor = "pointer";
          });
          mapRef.current.on("mouseleave", "parcel-fill", () => {
            mapRef.current.getCanvas().style.cursor = "";
          });

          setMapInitialized(true);
          fitMapToParcels(sortedParcels);
        });
      } catch (error) {
        console.error("❌ Error initializing map:", error);
      }
    };

    initMap();
    setTimeout(() => {
  mapRef.current.resize();
}, 100);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapInitialized(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [showMap, sortedParcels]);
  }, [showMap]);

  useEffect(() => {
  //  Only update map data if map is already initialized
  if (!mapRef.current || !mapInitialized) return;

  console.log("🔄 Updating map with", sortedParcels.length, "parcels");
  
  try {
    const source = mapRef.current.getSource("parcel-source");
    if (source) {
      source.setData({
        type: "FeatureCollection",
        features: sortedParcels,
      });
      fitMapToParcels(sortedParcels);
    }
  } catch (err) {
    console.error("❌ Error updating map data:", err);
  }
}, [sortedParcels, mapInitialized]);

// ✅ When map becomes visible
useEffect(() => {
  if (showMap && mapRef.current) {
    setTimeout(() => mapRef.current.resize(), 300);
  }
}, [showMap]);

  
  // RENDER
  
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* ========================================
          HEADER
          ======================================== */}
      <Header
        activeTab={"parcels"}
        handleTabChange={handleTabChange}
        toggleMenu={toggleMenu}
        isMenuOpen={isMenuOpen}
        handleLogout={handleLogout}
        geocoderContainerRef={geocoderContainerRef}
        showSearch={true}
        showFavorites={false}
        showLinks={true}
        isSubscribed={isSubscribed}
      />

      {/* ========================================
          FILTER BAR
          ======================================== */}
      <div className="subHeaderOuter" style={{ padding: "8px 12px" }}>
        <div
          className="filterOuter"
          style={{
            display: "flex",
            gap: "10px",
            padding: "10px",
            borderBottom: "1px solid #ddd",
            position: "relative",
          }}
        >
          {/* COUNTY FILTER */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowCountyFilter(!showCountyFilter);
              setShowPriceFilter(false);
              setShowAreaFilter(false);
              setShowZoneFilter(false);
              setShowZestimateFilter(false);
              setShowSpreadFilter(false);
              setShowSortFilter(false);
            }}
            style={{
              textTransform: "none",
              background: isCountyActive() ? "#1c8f7d" : "transparent",
              color: isCountyActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            {countyFilter === "All" ? "County" : `County - ${countyFilter}`}
          </Button>

      

          {/* LAND VALUE */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowPriceFilter(!showPriceFilter);
              setShowCountyFilter(false);
              setShowAreaFilter(false);
              setShowZoneFilter(false);
              setShowZestimateFilter(false);
              setShowSpreadFilter(false);
              setShowSortFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isPriceActive() ? "#1c8f7d" : "transparent",
              color: isPriceActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Land Value
          </Button>

          {/* PRICE (ZESTIMATE) */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowZestimateFilter(!showZestimateFilter);
              setShowCountyFilter(false);
              setShowPriceFilter(false);
              setShowAreaFilter(false);
              setShowZoneFilter(false);
              setShowSpreadFilter(false);
              setShowSortFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isZestimateActive() ? "#1c8f7d" : "transparent",
              color: isZestimateActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Price
          </Button>

          {/* SPREAD % */}
          {/* <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowSpreadFilter(!showSpreadFilter);
              setShowCountyFilter(false);
              setShowPriceFilter(false);
              setShowAreaFilter(false);
              setShowZoneFilter(false);
              setShowZestimateFilter(false);
              setShowSortFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isSpreadActive() ? "#1c8f7d" : "transparent",
              color: isSpreadActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Spread %
          </Button> */}

      {/*  ONLY SHOW MAX UNIT FILTER WHEN VACANT */}
{/* {isVacant && sliderMaxMaxUnit > 0 && (    */}
         <Button
  variant="contained"
  endIcon={<ArrowDropDownIcon />}
  onClick={() => {
    setShowMaxUnitFilter(!showMaxUnitFilter);
    setShowCountyFilter(false);
    setShowPriceFilter(false);
    setShowAreaFilter(false);
    setShowZoneFilter(false);
    setShowZestimateFilter(false);
    setShowSpreadFilter(false);
    setShowSortFilter(false);
  }}
  style={{
    textTransform: "none",
    background: isMaxUnitActive() ? "#1c8f7d" : "transparent",
    color: isMaxUnitActive() ? "#fff" : "#333",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    borderRadius: 6,
    fontSize: "12px",
  }}
>
  Max Unit
</Button>
{/* )} */}
          

          {/* PARCEL SIZE */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowAreaFilter(!showAreaFilter);
              setShowCountyFilter(false);
              setShowPriceFilter(false);
              setShowZoneFilter(false);
              setShowZestimateFilter(false);
              setShowSpreadFilter(false);
              setShowSortFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isAreaActive() ? "#1c8f7d" : "transparent",
              color: isAreaActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Parcel Size
          </Button>

          {/* ZONE CODE */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowZoneFilter(!showZoneFilter);
              setShowCountyFilter(false);
              setShowPriceFilter(false);
              setShowAreaFilter(false);
              setShowZestimateFilter(false);
              setShowSpreadFilter(false);
              setShowSortFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isZoneActive() ? "#1c8f7d" : "transparent",
              color: isZoneActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Zone Code
          </Button>

          {/* SORT BY */}
          <Button
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={() => {
              setShowSortFilter(!showSortFilter);
              setShowCountyFilter(false);
              setShowPriceFilter(false);
              setShowAreaFilter(false);
              setShowZoneFilter(false);
              setShowZestimateFilter(false);
              setShowSpreadFilter(false);
              setShowMaxUnitFilter(false); 
            }}
            style={{
              textTransform: "none",
              background: isSortActive() ? "#1c8f7d" : "transparent",
              color: isSortActive() ? "#fff" : "#333",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              borderRadius: 6,
              fontSize: "12px",
            }}
          >
            Sort By
          </Button>

              {/* VACANT CHECKBOX */}
          <div
            style={{
              color: "#007c6f",
              borderRadius: "20px",
              border: "solid 1px #007c6f",
              padding: "6px 15px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
            onClick={() => setIsVacant(!isVacant)}
          >
            <input
              type="checkbox"
              checked={isVacant}
              onChange={(e) => setIsVacant(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
                accentColor: "#1c8f7d",
              }}
            />
            <span
              style={{
                fontSize: "12px",
                fontWeight: isVacant ? "600" : "500",
                color: isVacant ? "#1c8f7d" : "#333",
              }}
            >
              Vacant Only
            </span>
          </div>

          {/* LISTED SALES CHECKBOX - Only show when isVacant is true */}
          {isVacant && (
            <div
              style={{
                color: "#007c6f",
                borderRadius: "20px",
                border: "solid 1px #007c6f",
                padding: "6px 15px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
              onClick={() => setShowOnlyListed(!showOnlyListed)}
            >
              <input
                type="checkbox"
                checked={showOnlyListed}
                onChange={(e) => setShowOnlyListed(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  accentColor: "#1c8f7d",
                }}
              />
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: showOnlyListed ? "600" : "500",
                  color: showOnlyListed ? "#1c8f7d" : "#333",
                }}
              >
                Listed for Sales
              </span>
            </div>
          )}

          {/* LISTED SALE TYPE - Only show when showOnlyListed is true */}

          {/* {showOnlyListed && (
            <div style={{ position: "relative" }}>
              <select
                value={listedSaleFilter}
                onChange={(e) => setListedSaleFilter(e.target.value)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #007c6f",
                  fontSize: "12px",
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                {listedSaleOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* RIGHT SECTION */}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
            {/* <span style={{ fontSize: "13px", color: "#666" }}>{sortedParcels.length} parcels</span> */}

            <Button
              variant="outlined"
              onClick={clearAllFilters}
              style={{
                textTransform: "none",
                borderColor: "#cfcfcf",
                color: "#333",
                fontSize: "12px",
              }}
            >
              Clear Filters
            </Button>

            {/* SHOW MAP BUTTON */}
            <Button
              variant="contained"
              onClick={() => setShowMap(!showMap)}
              disabled={!canShowMap()}
              style={{
                textTransform: "none",
                background: canShowMap() ? (showMap ? "#d32f2f" : "#1c8f7d") : "#ccc",
                color: "#fff",
                fontSize: "12px",
                cursor: canShowMap() ? "pointer" : "not-allowed",
              }}
            >
              {showMap ? "Hide Map" : "Show Map"}
            </Button>
          </div>
        </div>

        {/* ========================================
            FILTER DROPDOWNS
            ======================================== */}

        {/* COUNTY DROPDOWN */}
        {showCountyFilter && (
          <div
            style={{
              position: "absolute",
              top: 153,
              left: 30,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              borderRadius: "6px",
              zIndex: 999,
              padding: "6px 0",
              minWidth: "200px",
            }}
          >
            {countyOptions.map((county) => (
              <div
                key={county}
                onClick={() => {
                  setCountyFilter(county);
                  setShowCountyFilter(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: countyFilter === county ? "#1c8f7d" : "transparent",
                  color: countyFilter === county ? "#fff" : "#000",
                  fontSize: 13,
                }}
              >
                {county}
              </div>
            ))}
          </div>
        )}

        {/* LAND VALUE SLIDER */}
        {showPriceFilter && (
          <div
            className="price-filter-container"
            style={{
              position: "absolute",
              left: 185,
              top: 153,
            }}
          >
            <ReactSlider
              className="horizontal-slider"
              thumbClassName="example-thumb"
              trackClassName="example-track"
              value={priceRange}
              onChange={setPriceRange}
              min={sliderMinPrice}
              max={sliderMaxPrice}
              step={Math.max(1, Math.round((sliderMaxPrice - sliderMinPrice) / 100))}
              pearling
              minDistance={Math.max(1, Math.round((sliderMaxPrice - sliderMinPrice) / 20))}
            />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{formatCurrency(priceRange[0])}</span>
              <span>{formatCurrency(priceRange[1])}</span>
            </div>
          </div>
        )}

        {/* ZESTIMATE SLIDER */}
        {showZestimateFilter && (
          <div
            className="price-filter-container"
            style={{
              position: "absolute",
              left: 330,
              top: 153,
            }}
          >
            <ReactSlider
              className="horizontal-slider"
              thumbClassName="example-thumb"
              trackClassName="example-track"
              value={zestimateRange}
              onChange={setZestimateRange}
              min={sliderMinZestimate}
              max={sliderMaxZestimate}
              step={1000}
              pearling
              minDistance={10000}
            />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{formatCurrency(zestimateRange[0])}</span>
              <span>{formatCurrency(zestimateRange[1])}</span>
            </div>
          </div>
        )}

        {/* SPREAD % SLIDER */}
        {showSpreadFilter && (
          <div
            className="price-filter-container"
            style={{
              position: "absolute",
              left: 480,
              top: 153,
            }}
          >
            <ReactSlider
              className="horizontal-slider"
              thumbClassName="example-thumb"
              trackClassName="example-track"
              value={spreadRange}
              onChange={setSpreadRange}
              min={sliderMinSpread}
              max={sliderMaxSpread}
              step={1}
              pearling
              minDistance={2}
            />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{spreadRange[0]}%</span>
              <span>{spreadRange[1]}%</span>
            </div>
          </div>
        )}
{/* MAX UNIT SLIDER */}
{showMaxUnitFilter && (
  <div
    className="price-filter-container"
    style={{
      position: "absolute",
      left: 435,
      top: 153,
    }}
  >
    <ReactSlider
      className="horizontal-slider"
      thumbClassName="example-thumb"
      trackClassName="example-track"
      value={maxUnitRange}
      onChange={setMaxUnitRange}
      min={sliderMinMaxUnit}
      max={sliderMaxMaxUnit}
      step={1}
      pearling
      minDistance={1}
    />
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span>{Math.round(maxUnitRange[0])} units</span>
      <span>{Math.round(maxUnitRange[1])} units</span>
    </div>
  </div>
)}

       

        {/* AREA SLIDER */}
        {showAreaFilter && (
          <div
            className="price-filter-container"
            style={{
              position: "absolute",
              left: 565,
              top: 153,
            }}
          >
            <ReactSlider
              className="horizontal-slider"
              thumbClassName="example-thumb"
              trackClassName="example-track"
              value={areaRange}
              onChange={setAreaRange}
              min={sliderMinArea}
              max={sliderMaxArea}
              step={Math.max(1, Math.round((sliderMaxArea - sliderMinArea) / 100))}
              pearling
              minDistance={Math.max(1, Math.round((sliderMaxArea - sliderMinArea) / 20))}
            />
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{areaRange[0].toLocaleString()} sq ft</span>
              <span>{areaRange[1].toLocaleString()} sq ft</span>
            </div>
          </div>
        )}

        {/* ZONE CODE DROPDOWN */}
        {showZoneFilter && (
          <div
            style={{
              position: "absolute",
              top: 153,
              left: 707,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              borderRadius: "6px",
              zIndex: 999,
              padding: "6px 0",
              minWidth: "200px",
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            {zoneCodes.map((zc) => (
              <div
                key={zc}
                onClick={() => {
                  setZoneCodeFilter(zc);
                  setShowZoneFilter(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: zoneCodeFilter === zc ? "#1c8f7d" : "transparent",
                  color: zoneCodeFilter === zc ? "#fff" : "#000",
                  fontSize: 13,
                }}
              >
                {zc}
              </div>
            ))}
          </div>
        )}

        {/* SORT DROPDOWN */}
        {showSortFilter && (
          <div
            style={{
              position: "absolute",
              top: 153,
              left: 848,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              borderRadius: "6px",
              zIndex: 999,
              padding: "6px 0",
              minWidth: "200px",
            }}
          >
            {sortOptions.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  if (sortBy === opt.value) {
                    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                  } else {
                    setSortBy(opt.value);
                    setSortDirection("desc");
                  }
                  setShowSortFilter(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: sortBy === opt.value ? "#1c8f7d" : "transparent",
                  color: sortBy === opt.value ? "#fff" : "#000",
                  fontSize: 13,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{opt.label}</span>
                {sortBy === opt.value && (
                  <span style={{ fontSize: 11 }}>{sortDirection === "asc" ? "▲" : "▼"}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/*   MAIN CONTENT  */}
      <div className="mainOuter" style={{ display: "flex",flex:1 ,height: "100vh", overflow: "hidden" }}>
        {/* LIST */}
        <div
          className="listOuter"
          style={{
            width: showMap ? "50%" : "100%",
            height: "100%",
            borderRight: showMap ? "1px solid #ddd" : "none",
            background: "#fafafa",
            transition: "width 0.3s ease",
              overflow: "auto"
          }}
        >
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
          ) : error ? (
            <div style={{ padding: "20px", textAlign: "center", color: "red" }}>Error: {error}</div>
          ) : (
            <PropertyList parcels={sortedParcels} onRowClick={handleRowClick} />
          )}
        </div>

        {/* MAP */}
        {showMap && (
          <div style={{ width: "50%",  height: "100%",position: "relative", transition: "width 0.3s ease" }}>
            <div ref={mapContainer} style={{ width: "100%", height: "100%" ,position:"absolute",  top: 0,
          left: 0,
          right: 0,
          bottom: 0,}} />

            {/* FLOOD ZONE LEGEND */}
            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255, 255, 255, 0.95)",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
                fontSize: "11px",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              {FLOOD_ZONE_LEGEND.map((item) => (
                <div key={item.zone} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      backgroundColor: item.color,
                      opacity: 0.5,
                      borderRadius: "3px",
                      border: `2px solid ${item.color}`,
                    }}
                  />
                  <span style={{ whiteSpace: "nowrap" }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelScreenCarolinaMap;